/**
 * Maintenance Summary - Summary statistics for maintenance activities
 */
import { useMemo } from 'react';
import { Paper, Grid, Stack, Text, Group } from '@mantine/core';
import { IconTool, IconCheck, IconClock, IconPercentage } from '@tabler/icons-react';
import type { MaintenanceActivity } from '@/types/maintenance';
import { MAINTENANCE_SUMMARY_COLORS } from '@/utils/chartColors';

interface MaintenanceSummaryProps {
  activities: MaintenanceActivity[];
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}

function MetricCard({ icon, label, value, color }: MetricCardProps) {
  return (
    <Paper p="md" withBorder>
      <Stack gap="xs">
        <Group gap="xs">
          <div style={{ color }}>{icon}</div>
          <Text size="sm" c="dimmed" fw={500}>
            {label}
          </Text>
        </Group>
        <Text size="xl" fw={700}>
          {value}
        </Text>
      </Stack>
    </Paper>
  );
}

export function MaintenanceSummary({ activities }: MaintenanceSummaryProps) {
  const stats = useMemo(() => {
    const total = activities.length;
    const performed = activities.filter((a) => a.performed).length;
    const pending = total - performed;
    const completionRate = total > 0 ? ((performed / total) * 100).toFixed(1) : '0.0';

    return {
      total,
      performed,
      pending,
      completionRate,
    };
  }, [activities]);

  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
        <MetricCard
          icon={<IconTool size={20} />}
          label="Total Activities"
          value={stats.total}
          color={MAINTENANCE_SUMMARY_COLORS.total}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
        <MetricCard
          icon={<IconCheck size={20} />}
          label="Activities Performed"
          value={stats.performed}
          color={MAINTENANCE_SUMMARY_COLORS.completed}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
        <MetricCard
          icon={<IconClock size={20} />}
          label="Pending Activities"
          value={stats.pending}
          color={MAINTENANCE_SUMMARY_COLORS.overdue}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
        <MetricCard
          icon={<IconPercentage size={20} />}
          label="Completion Rate"
          value={`${stats.completionRate}%`}
          color={MAINTENANCE_SUMMARY_COLORS.compliance}
        />
      </Grid.Col>
    </Grid>
  );
}
